// auth.tsx — session context, read once from the bootstrap blob Flask renders.
//
// base.html emits <script id="cadrify-bootstrap" type="application/json"> with
// the current user, org, active group and CSRF token. React reads it for
// display and convenience only. It is NEVER the security boundary: every action
// hits a Flask endpoint that re-checks RBAC server-side, and islands render what
// the API returns, not what this context claims.
import { createContext, useContext, type ReactNode } from 'react'

export interface SessionUser {
  id: number
  name: string
  email: string
  isGod: boolean
}

export interface SessionGroup {
  id: number
  name: string
}

export interface Session {
  user: SessionUser | null
  orgId: number | null
  group: SessionGroup | null
  csrfToken: string
}

const EMPTY: Session = { user: null, orgId: null, group: null, csrfToken: '' }

export function readBootstrap(): Session {
  const el = document.getElementById('cadrify-bootstrap')
  if (!el?.textContent) return EMPTY
  try {
    const raw = JSON.parse(el.textContent) as Partial<Session>
    return { ...EMPTY, ...raw }
  } catch {
    return EMPTY
  }
}

const SessionContext = createContext<Session>(EMPTY)

export function AuthProvider({
  children,
  session,
}: {
  children: ReactNode
  session?: Session
}) {
  return (
    <SessionContext.Provider value={session ?? readBootstrap()}>
      {children}
    </SessionContext.Provider>
  )
}

export function useAuth(): Session {
  return useContext(SessionContext)
}
